import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import {
  useGetTeamMembers,
  usePostResendInviteTeamMember,
  useRelatedCustomers,
  useUser,
} from "@/requests/hooks/user";
import formatError from "@/utils/formatError";
import { faCircleNotch } from "@fortawesome/pro-duotone-svg-icons";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { tv } from "tailwind-variants";

type IStatus = 10 | 20;

const styles = tv({
  slots: {
    tableWrapper: "mt-30 w-full shadow-md",
    titleWrapper: "ml-20 mt-10 flex flex-row items-center gap-10",
    table: "w-full",
    thead: "border-t border-gray",
    th: "px-20 py-10 text-left",
    tbody: "",
    tr: "w-full border-y border-gray even:bg-gray-light/50",
    td: "px-20 py-10",
    emptyTable: "h-[200px] text-center",
    caretIcon: "h-15 w-15 text-secondary",
    placeholder:
      "flex h-[200px] w-full flex-grow flex-col items-center justify-center",
    placeholderBody: "flex flex-row items-center gap-10",
  },
});

const TeamMembersTable = () => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const {
    data: customers,
    isSuccess: isGetRelatedCustomerSuccess,
    isPending: isGetRelatedCustomerPending,
  } = useRelatedCustomers();

  const getCurrentCustomer = () =>
    customers?.results?.find(
      customer => customer.merchantId === user?.merchantId,
    );

  const {
    data: teamMembers,
    isPending: isGetTeamMembersPending,
    isError: isGetTeamMembersError,
    refetch: refetchGetTeamMembers,
    error: getTeamMembersError,
  } = useGetTeamMembers({
    enabled: !!user?.merchantId && !!customers?.results?.length,
    params: {
      merchantId: user?.merchantId?.toString(),
      customerPublicKey: getCurrentCustomer()?.publicKey,
    },
  });

  const {
    mutate: resendInviteTeamMember,
    isPending: isResendInvitePending,
    isSuccess: isResendInviteSuccess,
    isError: isResendInviteError,
  } = usePostResendInviteTeamMember();

  const {
    tableWrapper,
    titleWrapper,
    table: tableContainer,
    thead,
    th,
    tbody,
    tr,
    td,
    caretIcon,
    placeholder,
    placeholderBody,
  } = styles();

  useEffect(() => {
    if (isGetRelatedCustomerSuccess && !teamMembers?.results?.length) {
      refetchGetTeamMembers();
    }
  }, [isGetRelatedCustomerSuccess]);

  useEffect(() => {
    if (isResendInviteSuccess) {
      toast.success(t("settings.userManagement.resendInvite.success"));
    }
  }, [isResendInviteSuccess]);

  useEffect(() => {
    if (isResendInviteError) {
      toast.error(t("settings.userManagement.resendInvite.error"));
    }
  }, [isResendInviteError]);

  const handleResendInvite = (email: string) => {
    resendInviteTeamMember({
      email,
    });
  };

  const getBadgeData = (
    status: IStatus,
  ): { value: string; variant: "warning" | "success" } => {
    if (status == 10) {
      return {
        value: t("settings.userManagement.table.row.active"),
        variant: "success",
      };
    }
    return {
      value: t("settings.userManagement.table.row.inactive"),
      variant: "warning",
    };
  };

  const getErrorMessage = () => {
    const formattedError = formatError(getTeamMembersError);

    if (formattedError?.status === 403) {
      return t("generic.accessDeniedMessage");
    } else {
      return t("settings.userManagement.table.empty");
    }
  };

  const renderTableHead = () => (
    <thead className={thead()} data-testid="table-head">
      <tr>
        <th className={th()} role="cell">
          {t("settings.userManagement.table.email")}
        </th>
        <th className={th()} role="cell">
          {t("settings.userManagement.table.name")}
        </th>
        <th className={th()} role="cell">
          {t("settings.userManagement.table.status")}
        </th>
        <th className={th()} role="cell">
          {t("settings.userManagement.table.resendInvite")}
        </th>
        <th className={th()} role="cell">
          {t("settings.userManagement.table.edit")}
        </th>
      </tr>
    </thead>
  );

  const renderLoadingState = () => (
    <div className={placeholder()} data-testid="table-placeholder-container">
      <span className={placeholderBody()}>
        <FontAwesomeIcon fixedWidth icon={faCircleNotch} spin />
        {t("generic.loading")}
      </span>
    </div>
  );

  const renderEmptyState = () => (
    <div className={placeholder()}>
      <span className={placeholderBody()}>{getErrorMessage()}</span>
    </div>
  );

  const renderPlaceholderBody = () => {
    if (isGetRelatedCustomerPending || isGetTeamMembersPending) {
      return renderLoadingState();
    }

    if (
      !teamMembers ||
      teamMembers?.results?.length === 0 ||
      !!isGetTeamMembersError
    ) {
      return renderEmptyState();
    }
  };

  const renderStatusBadge = (status: IStatus) => {
    const { value, variant } = getBadgeData(status);
    return <Badge variant={variant}>{value.toUpperCase()}</Badge>;
  };

  const renderTableBody = () =>
    teamMembers?.results?.map(({ username, name, status, id }, i) => (
      <tr key={i} className={tr()} data-testid="table-row">
        <td className={td()} role="cell">
          {username}
        </td>
        <td className={td()} role="cell">
          {name}
        </td>
        <td className={td()} role="cell">
          {renderStatusBadge(status as IStatus)}
        </td>
        <td className={td()} role="cell">
          <Button
            as="button"
            variant="link"
            onClick={() => handleResendInvite(username)}
            loading={isResendInvitePending}
            disabled={user?.email === username}>
            {t("settings.userManagement.table.resendInvite")}
          </Button>
        </td>
        <td className={td()} role="cell">
          <Button
            as="link"
            variant="link"
            to={`edit?id=${id}`}
            disabled={user?.email === username}>
            {t("settings.userManagement.table.edit")}
            <FontAwesomeIcon icon={faCaretRight} className={caretIcon()} />
          </Button>
        </td>
      </tr>
    ));

  return (
    <>
      <div className={tableWrapper()}>
        <div className={titleWrapper()}></div>
        <table role="table" className={tableContainer()}>
          {renderTableHead()}
          <tbody className={tbody()} data-testid="table-body">
            {renderTableBody()}
          </tbody>
        </table>
        {renderPlaceholderBody()}
      </div>
    </>
  );
};

export default TeamMembersTable;
